import {AssayResult} from 'results/assays/AssayRecordConstants';
import {PostProcessResultsBySchema, ResultsBySchemaId} from 'results/assays/FilteredAssayResultsActions';

/**
 * Legacy Request fulfillment result ordering.
 *
 * The legacy Request fulfillment tab historically displayed results as `created_at DESC, id ASC`:
 * newest submissions first, but results submitted together in one batch (which share a `created_at`)
 * in ascending submission order. The v2 results-listing pagination changed the within-timestamp
 * tiebreak to `id DESC`, reversing each batch (see CUST-12179 / BNCH-160489).
 *
 * We restore the old order on the client. The backend still returns `created_at DESC, id DESC`, and
 * the result payload only exposes a random warehouse UUID as `id` (not the numeric DB id used for the
 * backend tiebreak), so we cannot sort by id directly. Instead we reverse each contiguous run of
 * equal-`createdAt` results: that flips the backend's `id DESC` tiebreak into `id ASC` while leaving
 * the `created_at DESC` primary order intact. This relies on the backend returning equal-`createdAt`
 * rows contiguously in `id DESC` order, which is the `sort=createdAt:desc` contract.
 */
function reverseTiesWithinTimestamp(results: Array<AssayResult>): Array<AssayResult> {
  const reordered: Array<AssayResult> = [];
  let runStart = 0;
  while (runStart < results.length) {
    let runEnd = runStart;
    while (runEnd < results.length && results[runEnd].createdAt === results[runStart].createdAt) {
      runEnd++;
    }
    for (let i = runEnd - 1; i >= runStart; i--) {
      reordered.push(results[i]);
    }
    runStart = runEnd;
  }
  return reordered;
}

/**
 * Post-processing hook for `StandaloneResultsView` that restores the legacy fulfillment ordering
 * (`created_at DESC, id ASC`) on each schema's results. See `reverseTiesWithinTimestamp`.
 */
export const sortResultsForFulfillment: PostProcessResultsBySchema = (
  resultsBySchema: ResultsBySchemaId
): ResultsBySchemaId => {
  const reordered: ResultsBySchemaId = {};
  for (const [schemaId, results] of Object.entries(resultsBySchema)) {
    reordered[schemaId] = reverseTiesWithinTimestamp(results);
  }
  return reordered;
};
